import { createFeature, createReducer, on } from "@ngrx/store";
import {
    GroupMutationState,
    GroupPerson,
    GroupSection,
    ManagedGroup,
} from "../models/group.models";
import { GroupsActions } from "./groups.actions";

export interface GroupsState {
    groups: ManagedGroup[];
    selectedGroupId: string | null;
    members: GroupPerson[];
    admins: GroupPerson[];
    groupQuery: string;
    peopleQuery: string;
    section: GroupSection;
    loadingGroups: boolean;
    loadingPeople: boolean;
    loaded: boolean;
    error: boolean;
    mutation: GroupMutationState | null;
}

const initialState: GroupsState = {
    groups: [],
    selectedGroupId: null,
    members: [],
    admins: [],
    groupQuery: "",
    peopleQuery: "",
    section: "members",
    loadingGroups: false,
    loadingPeople: false,
    loaded: false,
    error: false,
    mutation: null,
};

export const groupsFeature = createFeature({
    name: "groups",
    reducer: createReducer(
        initialState,
        on(
            GroupsActions.pageOpened,
            (state): GroupsState => ({
                ...state,
                loadingGroups: true,
                error: false,
            })
        ),
        on(GroupsActions.groupsLoaded, (state, { groups }): GroupsState => {
            const selectedGroupId = groups.some(
                (group) => group.gmvid === state.selectedGroupId
            )
                ? state.selectedGroupId
                : (groups[0]?.gmvid ?? null);
            return {
                ...state,
                groups,
                selectedGroupId,
                loadingGroups: false,
                loaded: true,
                error: false,
            };
        }),
        on(
            GroupsActions.groupsLoadFailed,
            (state): GroupsState => ({
                ...state,
                loadingGroups: false,
                loaded: true,
                error: true,
            })
        ),
        on(
            GroupsActions.groupSelected,
            (state, { groupId }): GroupsState => ({
                ...state,
                selectedGroupId: groupId,
                members: [],
                admins: [],
                peopleQuery: "",
                loadingPeople: true,
                error: false,
            })
        ),
        on(
            GroupsActions.peopleLoaded,
            (state, { groupId, members, admins }): GroupsState =>
                state.selectedGroupId === groupId
                    ? {
                          ...state,
                          members,
                          admins,
                          loadingPeople: false,
                          error: false,
                      }
                    : state
        ),
        on(
            GroupsActions.peopleLoadFailed,
            (state, { groupId }): GroupsState =>
                state.selectedGroupId === groupId
                    ? { ...state, loadingPeople: false, error: true }
                    : state
        ),
        on(
            GroupsActions.groupQueryChanged,
            (state, { query }): GroupsState => ({ ...state, groupQuery: query })
        ),
        on(
            GroupsActions.peopleQueryChanged,
            (state, { query }): GroupsState => ({
                ...state,
                peopleQuery: query,
            })
        ),
        on(
            GroupsActions.sectionChanged,
            (state, { section }): GroupsState => ({
                ...state,
                section,
                peopleQuery: "",
            })
        ),
        on(
            GroupsActions.createRequested,
            (state): GroupsState => ({
                ...state,
                mutation: { type: "create", status: "pending" },
            })
        ),
        on(
            GroupsActions.createSucceeded,
            (state, { group }): GroupsState => ({
                ...state,
                groups: [...state.groups, group].sort((a, b) =>
                    a.name.localeCompare(b.name)
                ),
                selectedGroupId: group.gmvid,
                members: [],
                admins: [],
                section: "members",
                mutation: { type: "create", status: "success" },
            })
        ),
        on(
            GroupsActions.renameRequested,
            (state): GroupsState => ({
                ...state,
                mutation: { type: "rename", status: "pending" },
            })
        ),
        on(
            GroupsActions.renameSucceeded,
            (state, { group }): GroupsState => ({
                ...state,
                groups: state.groups
                    .map((current) =>
                        current.gmvid === group.gmvid ? group : current
                    )
                    .sort((a, b) => a.name.localeCompare(b.name)),
                mutation: { type: "rename", status: "success" },
            })
        ),
        on(
            GroupsActions.deleteRequested,
            (state): GroupsState => ({
                ...state,
                mutation: { type: "delete", status: "pending" },
            })
        ),
        on(GroupsActions.deleteSucceeded, (state, { groupId }): GroupsState => {
            const groups = state.groups.filter(
                (group) => group.gmvid !== groupId
            );
            return {
                ...state,
                groups,
                selectedGroupId: groups[0]?.gmvid ?? null,
                members: [],
                admins: [],
                mutation: { type: "delete", status: "success" },
            };
        }),
        on(
            GroupsActions.addMembersRequested,
            (state): GroupsState => ({
                ...state,
                mutation: { type: "add-members", status: "pending" },
            })
        ),
        on(
            GroupsActions.removeMembersRequested,
            (state): GroupsState => ({
                ...state,
                mutation: { type: "remove-members", status: "pending" },
            })
        ),
        on(
            GroupsActions.addAdminsRequested,
            (state): GroupsState => ({
                ...state,
                mutation: { type: "add-admins", status: "pending" },
            })
        ),
        on(
            GroupsActions.removeAdminsRequested,
            (state): GroupsState => ({
                ...state,
                mutation: { type: "remove-admins", status: "pending" },
            })
        ),
        on(
            GroupsActions.peopleMutationSucceeded,
            (state): GroupsState => ({
                ...state,
                mutation: state.mutation
                    ? { ...state.mutation, status: "success" }
                    : null,
            })
        ),
        on(
            GroupsActions.mutationFailed,
            (state): GroupsState => ({
                ...state,
                mutation: state.mutation
                    ? { ...state.mutation, status: "error" }
                    : null,
            })
        ),
        on(
            GroupsActions.mutationStatusCleared,
            (state): GroupsState => ({ ...state, mutation: null })
        )
    ),
});
